import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useSearchParams } from "react-router";
import type { RoundName } from "../../domain/round/types";
import { vehicleVisualIdentity } from "../../domain/transport/identity";
import type { TransportDirection, VehicleTrip } from "../../domain/transport/types";
import type { RoundWorkspacePayload } from "../../lib/api";
import { ManualBoardTable, type ManualBoardTableCallbacks } from "../manual/ManualBoardTable";
import "../manual/manual-board.css";
import "../manual/manual-board-operations.css";
import "../transport/transport-overview.css";
import "./discord-snapshot.css";

function clock(minutes: number) {
  const value = Math.max(0, Math.round(minutes || 0));
  return `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
}

function TransportDirectionView({ direction, trips, people, vehicles, bases }: {
  direction: TransportDirection;
  trips: VehicleTrip[];
  people: Map<string, string>;
  vehicles: Map<string, { name?: string; capacity?: number; baseName?: string }>;
  bases: Map<string, string>;
}) {
  const title = direction === "to-course" ? "Dojazd na pole" : "Powrót z pola";
  const eyebrow = direction === "to-course" ? "TO COURSE" : "RETURN";
  return (
    <section className={`transport-overview-direction ${direction}`} data-snapshot={direction === "to-course" ? "arrival" : "return"}>
      <header><div><span className="eyebrow">{eyebrow}</span><h3>{title}</h3></div><strong>{trips.length} kursów</strong></header>
      <div className="transport-overview-cars">
        {trips.map((trip) => {
          const visual = vehicleVisualIdentity(trip.vehicleId);
          const style = { "--car-bg": visual.background, "--car-border": visual.border, "--car-fg": visual.foreground } as CSSProperties;
          const vehicle = vehicles.get(trip.vehicleId);
          const members = [trip.driverPersonId, ...trip.passengerPersonIds].filter(Boolean);
          return (
            <article className="transport-overview-car" style={style} key={trip.id}>
              <div className="transport-overview-car-head">
                <div><strong>{vehicle?.name || trip.vehicleId}</strong><small>{bases.get(trip.baseId) || vehicle?.baseName || "—"}</small></div>
                <span>{members.length}{vehicle?.capacity ? ` / ${vehicle.capacity}` : ""} os.</span>
              </div>
              <div className="transport-overview-meta">
                <span><small>{direction === "to-course" ? "WYJAZD" : "ODJAZD"}</small><strong>{clock(trip.departAtMinutes)}</strong></span>
                <span><small>PRZYJAZD</small><strong>{clock(trip.arriveAtMinutes)}</strong></span>
                <span><small>SPREAD</small><strong>{trip.waitSpreadMinutes} min</strong></span>
                <span><small>KURS</small><strong>{trip.shuttle ? `SHUTTLE ${trip.tripNumber}` : trip.tripNumber}</strong></span>
              </div>
              <table><thead><tr><th>#</th><th>Osoba</th><th>Rola</th></tr></thead><tbody>{members.map((personId, index) => {
                const driver = personId === trip.driverPersonId;
                return <tr key={`${trip.id}-${personId}`} className={driver ? "driver" : "passenger"}><td>{index + 1}</td><td>{people.get(personId) || personId}</td><td>{driver ? "KIEROWCA" : "PASAŻER"}</td></tr>;
              })}</tbody></table>
            </article>
          );
        })}
        {!trips.length ? <div className="transport-overview-empty">Brak kursów w tym kierunku.</div> : null}
      </div>
    </section>
  );
}

function endpoint(tournamentId: string, round: RoundName, jobId: string, proposal: string) {
  if (jobId && /^\d+$/.test(proposal)) {
    return `/api/internal/discord/positions/${encodeURIComponent(tournamentId)}/jobs/${encodeURIComponent(jobId)}/proposal/${proposal}/export-workspace`;
  }
  return `/api/internal/discord/positions/${encodeURIComponent(tournamentId)}/${round}/export-workspace`;
}

export function DiscordSnapshotPage() {
  const [params] = useSearchParams();
  const tournamentId = String(params.get("tournament") || "").trim();
  const requestedRound = String(params.get("round") || "R1").toUpperCase();
  const round = (["R1", "R2", "R3", "R4"].includes(requestedRound) ? requestedRound : "R1") as RoundName;
  const jobId = String(params.get("job") || "").trim();
  const proposal = String(params.get("proposal") || "").trim();
  const [workspace, setWorkspace] = useState<RoundWorkspacePayload | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setWorkspace(null);
    setError("");
    if (!tournamentId) { setError("Brak turnieju."); return; }
    void fetch(endpoint(tournamentId, round, jobId, proposal), { headers: { Accept: "application/json" } })
      .then(async (response) => {
        const body = await response.json().catch(() => null);
        if (!response.ok) throw new Error(String(body?.detail || `HTTP ${response.status}`));
        return body;
      })
      .then((body) => { if (!cancelled) setWorkspace(body?.workspace || null); })
      .catch((exc) => { if (!cancelled) setError(exc instanceof Error ? exc.message : String(exc)); });
    return () => { cancelled = true; };
  }, [tournamentId, round, jobId, proposal]);

  const people = useMemo(() => {
    const result = new Map<string, string>();
    for (const row of workspace?.staff || []) result.set(row.personId, row.displayName);
    for (const row of workspace?.schedule?.people || []) result.set(row.personId, row.displayName);
    return result;
  }, [workspace]);
  const vehicles = useMemo(() => new Map((workspace?.resources.vehicles || []).map((row) => [row.id, row])), [workspace]);
  const bases = useMemo(() => new Map((workspace?.resources.bases || []).map((row) => [row.id, row.name])), [workspace]);

  const callbacks = useMemo<ManualBoardTableCallbacks>(() => ({
    readOnly: true,
    selectedAssignmentId: "",
    selectedQuickPersonId: "",
    selectedQuickPersonName: "",
    peopleById: people,
    onAssignmentClick: () => undefined,
    onMoveSelected: () => undefined,
    onAssignQuickPerson: () => undefined,
    onOpenPersonPicker: () => undefined,
    onToggleLock: () => undefined,
    onRemove: () => undefined,
  }), [people]);

  if (error) return <main className="discord-snapshot-error" data-error="1">{error}</main>;
  if (!workspace) return <main className="discord-snapshot-loading">Wczytuję…</main>;

  return (
    <main className="discord-snapshot-root" data-ready="1">
      {workspace.schedule ? <div className="mbo-board-area discord-snapshot-board" data-snapshot="positions"><ManualBoardTable schedule={workspace.schedule} callbacks={callbacks} /></div> : null}
      {workspace.transport ? <>
        <TransportDirectionView direction="to-course" trips={workspace.transport.departures} people={people} vehicles={vehicles} bases={bases} />
        <TransportDirectionView direction="return" trips={workspace.transport.returns} people={people} vehicles={vehicles} bases={bases} />
      </> : null}
    </main>
  );
}
